import axios from 'axios';

import { KanzhongBackend } from 'constant/KanzhongBackend';
import { ServerError, createApiError } from 'stores/ApiClientError';

// --- 型定義 ---

export const NoticeType = {
  NOTICE: '通知',
  INFO: 'お知らせ',
  IMPORTANT: '重要なお知らせ',
  SECURITY_CHECK: 'セキュリティチェック',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type NoticeType = keyof typeof NoticeType;

/** NoticeType キー定数（文字列リテラルのハードコーディング防止用） */
export const NOTICE_TYPE_KEY = {
  NOTICE: 'NOTICE' as NoticeType,
  INFO: 'INFO' as NoticeType,
  IMPORTANT: 'IMPORTANT' as NoticeType,
  SECURITY_CHECK: 'SECURITY_CHECK' as NoticeType,
} as const;

export type ListAdminNoticeParams = {
  noticeType?: NoticeType;
  userId?: string;
  hasDeadline?: boolean;
  titleKeyword?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  isBroadcast?: boolean;
};

export type CreateAdminNoticeParams = {
  noticeType: NoticeType;
  title: string;
  content: string;
  externalLink?: string;
  deadline?: string;
  priority?: number;
  userIds?: string[];
};

export type UpdateAdminNoticeParams = {
  noticeId: string;
  title: string;
  content: string;
  externalLink?: string;
  deadline?: string;
  priority: number;
};

export type DeleteAdminNoticeParams = {
  noticeId: string;
};

export type ResolveAdminNoticeParams = {
  noticeId: string;
};

export type AdminNoticeResult = {
  noticeId: string;
  noticeType: NoticeType;
  title: string;
  content: string;
  externalLink?: string;
  internalLink?: string;
  deadline?: string;
  priority: number;
  isSystemNotice: boolean;
  isBroadcast: boolean;
  isResolved: boolean;
  createdAt: string;
  userIds?: string[];
  resolvedUserIds?: string[];
  unresolvedUserIds?: string[];
};

// --- API関数 ---

/**
 * 通知一覧取得（管理者）API
 * @param params.noticeType - 通知種別（任意, "NOTICE" | "INFO" | "IMPORTANT"）
 * @param params.userId - ユーザーID（任意）
 * @param params.hasDeadline - 期限あり（任意, trueの場合、期限が設定されているもののみ）
 * @param params.titleKeyword - タイトルキーワード（任意, 部分一致）
 * @param params.createdAtFrom - 作成日FROM（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.createdAtTo - 作成日TO（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.isBroadcast - ブロードキャストフラグ（任意）
 */
export const listAdminNotice = async (
  params: ListAdminNoticeParams,
): Promise<AdminNoticeResult[]> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    const response = await axios.post(KanzhongBackend.LIST_ADMIN_NOTICE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });

    return response.data.results;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 通知登録（管理者）API
 * @param params.noticeType - 通知種別（必須, 最大300文字, "NOTICE" | "INFO" | "IMPORTANT"）
 * @param params.title - タイトル（必須, 最大300文字）
 * @param params.content - 内容（必須, 最大10000文字）
 * @param params.externalLink - 外部リンク（任意, 最大500文字）
 * @param params.deadline - 期限（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.priority - 優先度（任意, 1〜5, 1が最高、未指定時は5）
 * @param params.userIds - 対象ユーザーIDリスト（NOTICEの場合必須、INFO/IMPORTANTは不要）
 */
export const createAdminNotice = async (params: CreateAdminNoticeParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.CREATE_ADMIN_NOTICE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 通知更新（管理者）API
 * @param params.noticeId - 通知ID（必須, 最大300文字）
 * @param params.title - タイトル（必須, 最大300文字）
 * @param params.content - 内容（必須, 最大10000文字）
 * @param params.externalLink - 外部リンク（任意, 最大500文字）
 * @param params.deadline - 期限（任意, format: yyyy-MM-dd'T'HH:mm:ss）
 * @param params.priority - 優先度（必須, 1〜5, 1が最高）
 */
export const updateAdminNotice = async (params: UpdateAdminNoticeParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.UPDATE_ADMIN_NOTICE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 通知削除（管理者）API
 * @param params.noticeId - 通知ID（必須, 最大300文字）
 */
export const deleteAdminNotice = async (params: DeleteAdminNoticeParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.DELETE_ADMIN_NOTICE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};

/**
 * 通知解決（管理者）API
 * @param params.noticeId - 通知ID（必須, 最大300文字）
 */
export const resolveAdminNotice = async (params: ResolveAdminNoticeParams): Promise<void> => {
  const sessionId = localStorage.getItem('session-id');

  try {
    await axios.post(KanzhongBackend.RESOLVE_ADMIN_NOTICE, params, {
      headers: {
        'X-Session-Id': sessionId,
      },
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const { status, data } = error.response;
      throw createApiError(status, data?.message || error.message);
    }
    throw new ServerError();
  }
};
